import React, { useEffect, useState } from 'react';

import { TextInput, TextInputProps } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import { IconX } from '@tabler/icons-react';

interface DebounceSearchInputProps extends TextInputProps {
  setSearchValue: (value: string | undefined) => void;
  searchValue: string | undefined;
  debounceRate?: number;
  onSearch?: (value: string | undefined) => void;
}

const DebounceSearchInput: React.FC<DebounceSearchInputProps> = ({
  setSearchValue,
  searchValue,
  debounceRate = 300,
  onSearch,
  ...textInputProps
}) => {
  const [localValue, setLocalValue] = useState(searchValue);

  // Create a debounced callback
  const debouncedSearch = useDebouncedCallback((value: string) => {
    setSearchValue(value);
    onSearch?.(value);
  }, debounceRate);

  // Handle input change
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.currentTarget.value;
    setLocalValue(newValue);
    debouncedSearch(newValue);

    // Call original onChange if provided
    textInputProps.onChange?.(event);
  };

  // Sync localValue with prop changes
  useEffect(() => {
    setLocalValue(searchValue);
  }, [searchValue]);

  // Clear search field and set search value to undefined
  const handleClear = () => {
    setLocalValue('');
    setSearchValue(undefined);
    onSearch?.(undefined);
  };

  return (
    <TextInput
      value={localValue}
      onChange={handleChange}
      {...textInputProps}
      rightSection={
        (searchValue || localValue) && (
          <IconX size={16} onClick={handleClear} style={{ cursor: 'pointer' }} />
        )
      }
    />
  );
};

export default DebounceSearchInput;
